import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getJournal } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";

/**
 * Journal post page — reads body as plain text with \n paragraph breaks.
 * URL: /journal/:slug
 */
const JournalPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useSEO({ title: post ? `${post.title} · GOYA LISBOA · Journal` : "Journal · GOYA LISBOA" });

  useEffect(() => {
    setLoading(true);
    getJournal(slug)
      .then(setPost)
      .catch((e) => setError(e?.response?.status === 404 ? "not-found" : "error"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="pt-28 md:pt-36 pb-24 px-6 md:px-12 text-sm text-[#8A7568]">A carregar…</div>;

  if (error === "not-found") {
    return (
      <div className="pt-28 md:pt-36 pb-24 px-6 md:px-12 max-w-2xl" data-testid="journal-post-not-found">
        <div className="font-badge text-[10px] tracking-[0.32em] uppercase text-[var(--gl-orange-hover)]">404</div>
        <h1 className="font-serif-display text-4xl md:text-6xl mt-3 tracking-tight">História não encontrada.</h1>
        <p className="mt-6 text-[#8A7568]">A história que procuras já não está disponível.</p>
        <Link to="/journal" className="btn-orange mt-8 inline-block">← Voltar ao Journal</Link>
      </div>
    );
  }

  if (!post) return null;

  return (
    <article className="pt-28 md:pt-36 pb-24 max-w-3xl mx-auto px-6 md:px-12" data-testid="journal-post-page">
      <Link to="/journal" className="font-badge text-[10px] tracking-[0.28em] uppercase text-[var(--gl-orange-hover)] hover:underline">
        ← Journal
      </Link>
      <h1 className="font-serif-display text-4xl md:text-6xl mt-4 leading-tight tracking-tight text-[#241C18]">
        {post.title}
      </h1>
      {post.image_url && (
        <div className="mt-10 aspect-[3/2] overflow-hidden">
          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}
      {post.excerpt && (
        <div className="mt-8 md:mt-10 font-serif-display text-xl md:text-2xl leading-relaxed text-[#241C18] italic border-l-2 border-[var(--gl-orange)] pl-5">
          {post.excerpt}
        </div>
      )}
      <div className="mt-8 md:mt-10 space-y-4 text-[#241C18] leading-relaxed">
        {(post.body || "").split(/\n\n+/).map((para, i) => (
          <p key={i} className="whitespace-pre-line">{para}</p>
        ))}
      </div>
    </article>
  );
};

export default JournalPost;
